import type { RoomLayout, Questionnaire } from '../types.ts';
import { AVAILABLE_GEMINI_MODELS } from '../models.ts';

/**
 * Supported zone categories based on client specifications.
 */
export type ZoneType =
  | 'living'
  | 'dining'
  | 'kitchen'
  | 'workplace'
  | 'playground'
  | 'relax';

/**
 * Metadata definition for a zone category.
 */
export interface ZoneCategoryDefinition {
  id: string;              // Discriminator ID (e.g. 'living-id')
  type: ZoneType;          // Internal zone type ('living')
  nameCs: string;          // Czech display name ('Obývací zóna')
  nameEn: string;          // English display name ('Living Zone')
  descriptionCs: string;   // Czech description
  descriptionEn: string;   // English description
  typicalFurniture: string[]; // Key representative furniture items
  color: string;           // Translucent fill color for SVG
  strokeColor: string;     // Border stroke color for SVG
  badgeColor: string;      // Color for UI pills and badges
}

/**
 * Polygon Point [x, z] in millimeters relative to room center (0,0) as returned by Discriminator.
 */
export type Point2D = [number, number];

/**
 * Evaluated geometric zone partition.
 */
export interface Zone {
  id: string;              // Zone ID (e.g. 'living-id' or custom ID)
  type: ZoneType;          // Category type
  name: string;            // Display title (e.g. 'Obývací zóna')
  polygon: Point2D[];      // Outer ring vertices in mm relative to room center
  color: string;           // Translucent fill
  strokeColor: string;     // Border color
  badgeColor: string;
  area: number;            // Surface area in square meters (m²)
  areaPercent: number;     // Percentage of total room floor area (0-100%)
  centroid: [number, number]; // [x_mm, z_mm] relative to room center
  assignedObjectIds?: string[]; // IDs of furniture items placed inside this zone
}

/**
 * AI-generated spatial zone anchor object.
 */
export interface ZoneAnchorObject {
  id: string;
  name: string;
  zoneId: string;
  zoneType: ZoneType;
  position: {
    x: number; // in meters relative to bottom-left (0 to room.width)
    y: number; // in meters relative to bottom-left (0 to room.length)
    z: number; // in meters
  };
  size: {
    width: number;  // X in meters
    length: number; // Y in meters
    height: number; // Z in meters
  };
  rotationDegrees: number;
  isVirtualAnchor?: boolean;
}

/**
 * A distinct architectural zoning variant.
 */
export interface ZoningVariant {
  variantId: number;
  title: string;
  description: string;
  tradeOffs?: string;
  score?: number;
  confidence?: 'high' | 'medium' | 'low';
  selectedZoneTypes: ZoneType[];
  itemZoneAssignments: Array<{ itemId: string; zoneId: string }>;
  zoneAnchors: ZoneAnchorObject[];
  zones: Zone[];
  updatedRoomData: RoomLayout;
}

/**
 * Request payload sent to the Zoning Service.
 */
export interface ZoningRequest {
  roomData: RoomLayout;
  prompt: string;
  questionnaire?: Questionnaire;
  model?: string;
  activeZoneTypes?: ZoneType[];
  customRulesMarkdown?: string;
  includeAllSkills?: boolean;
}

/**
 * Response structure returned by the Zoning Service.
 */
export interface ZoningResult {
  success: boolean;
  variants: ZoningVariant[];
  activeVariantIndex: number;
  zones: Zone[];
  updatedRoomData: RoomLayout;
  aiExplanation?: string;
  generatedAnchors?: ZoneAnchorObject[];
  executionTime?: number;
  logs?: Array<{
    stage: string;
    message: string;
    timestamp: string;
    data?: any;
  }>;
  error?: string;
}

/**
 * List of supported models for zoning AI reasoning (unified from models.ts).
 */
export const AVAILABLE_ZONING_MODELS = AVAILABLE_GEMINI_MODELS;
